/**
 * @fileoverview Firebase Cloud Messaging device token retrieval.
 * @description Registers the app's background messaging service worker (public/firebase-
 * messaging-sw.js) and exchanges it for an FCM device token. firebase/messaging is loaded via a
 * dynamic import, rather than a static one, so its code never enters the main bundle for the
 * common case (no Firebase project configured) — it only loads once getFcmToken actually needs
 * it. The service worker itself is registered with the Firebase config passed as query params on
 * its URL, since a static public/ asset isn't run through Vite's import.meta.env substitution the
 * way application code is.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { env } from '@/config/env'
import { getFirebaseApp } from '@/config/firebase'
import { firebaseConfig } from '@/config/firebase-config'
import { isPushNotificationSupported } from '@/shared/lib/notifications/notification-permission.utils'
import { logger } from '@/shared/utils/logger'

const MESSAGING_SERVICE_WORKER_PATH = '/firebase-messaging-sw.js'

/**
 * @description Builds the messaging service worker's registration URL, carrying the Firebase
 * config as query params so the worker can initialize Firebase without reading env vars.
 * @returns {string} The service worker URL to register.
 */
function buildServiceWorkerUrl(): string {
  const params = new URLSearchParams({
    apiKey: firebaseConfig.apiKey,
    authDomain: firebaseConfig.authDomain,
    projectId: firebaseConfig.projectId,
    storageBucket: firebaseConfig.storageBucket,
    messagingSenderId: firebaseConfig.messagingSenderId,
    appId: firebaseConfig.appId,
  })

  return `${MESSAGING_SERVICE_WORKER_PATH}?${params.toString()}`
}

/**
 * @description Registers the messaging service worker and retrieves an FCM device token for the
 * current browser. Resolves to null (rather than throwing) when Firebase isn't configured, push
 * notifications aren't supported, no VAPID key is set, or registration/token retrieval fails.
 * @returns {Promise<string | null>} The FCM device token, or null.
 */
export async function getFcmToken(): Promise<string | null> {
  const app = getFirebaseApp()

  if (!app || !env.firebaseVapidKey || !isPushNotificationSupported()) {
    return null
  }

  try {
    const registration = await navigator.serviceWorker.register(buildServiceWorkerUrl())
    const { getMessaging, getToken } = await import('firebase/messaging')
    const messaging = getMessaging(app)

    // getToken is marked @deprecated in favor of the newer register()/onRegistered() Firebase
    // Installation ID (FID) flow, but that flow is too new to have a mature Admin SDK send story
    // to build a backend against yet. getToken's registration-token model is what backends
    // sending via the Firebase Admin SDK still expect today — this is a deliberate choice, not an
    // oversight, revisit once FID-based sending is well-established server-side.
    return await getToken(messaging, {
      vapidKey: env.firebaseVapidKey,
      serviceWorkerRegistration: registration,
    })
  } catch (error) {
    logger.warn('Failed to retrieve FCM device token', { error })
    return null
  }
}
